import React, { useState } from "react";
import styles from "./BlogSecRight.module.css";
import {
  FaFacebookF,
  FaLinkedinIn,
  FaEnvelope,
  FaCalendarAlt,
  FaEye,
  FaCalendar,
} from "react-icons/fa";
import { RiInstagramFill, RiWhatsappFill } from "react-icons/ri";
import DatePickerField from "@/Common/DatePickerField";
import { withRegionPath } from "@/utils/session";
import { useAlert } from "@/Common/CustomAlert/AlertContext";
import { saveBlogSubscriber, submitBlogEnquiry } from "@/services/api/blog";
import {
  BLOG_TRAVELLER_OPTIONS,
  buildBlogEnquiryPayload,
  buildBlogSubscribePayload,
  createBlogEnquiryFormState,
  createBlogSubscribeFormState,
  getBlogApiErrorMessage,
  isBlogApiSuccess,
  validateBlogEnquiryForm,
  validateBlogSubscribeForm,
} from "@/utils/blogFormHelpers";
import PhoneInputField from "@/Common/PhoneInputField";

export default function BlogSecRight({ postData, recentBlogs = [], onShareClick }) {
  const { showAlert } = useAlert();

  const [enquiryForm, setEnquiryForm] = useState(createBlogEnquiryFormState);
  const [subscribeForm, setSubscribeForm] = useState(createBlogSubscribeFormState);
  const [enquiryErrors, setEnquiryErrors] = useState({});
  const [subscribeErrors, setSubscribeErrors] = useState({});
  const [isSubmittingEnquiry, setIsSubmittingEnquiry] = useState(false);
  const [isSubmittingSubscribe, setIsSubmittingSubscribe] = useState(false);

  const tags = postData?.tags?.length
    ? postData.tags
    : [
      "Top 50 Attractions in Dubai to visit",
      "Travel Guide Dubai",
      "Dubai Blog",
      "Hidden Places to Visit in Dubai",
    ];

  const recentPosts = recentBlogs;

  const updateEnquiryField = (field, value) => {
    setEnquiryForm((prev) => ({ ...prev, [field]: value }));
    setEnquiryErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const updateSubscribeField = (field, value) => {
    setSubscribeForm((prev) => ({ ...prev, [field]: value }));
    setSubscribeErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleSubscribeSubmit = async (event) => {
    event.preventDefault();

    const errors = validateBlogSubscribeForm(subscribeForm);
    if (Object.keys(errors).length > 0) {
      setSubscribeErrors(errors);
      return;
    }

    setIsSubmittingSubscribe(true);
    try {
      const response = await saveBlogSubscriber(
        buildBlogSubscribePayload(subscribeForm)
      );

      if (!isBlogApiSuccess(response)) {
        throw new Error(response?.message || "Unable to subscribe.");
      }

      showAlert({
        title: "Subscribed",
        message: response?.message || "You have subscribed successfully.",
      });
      setSubscribeForm(createBlogSubscribeFormState());
      setSubscribeErrors({});
    } catch (error) {
      showAlert({
        title: "Subscription failed",
        message: getBlogApiErrorMessage(error, "Unable to subscribe."),
      });
    } finally {
      setIsSubmittingSubscribe(false);
    }
  };

  const handleEnquirySubmit = async (event) => {
    event.preventDefault();

    const errors = validateBlogEnquiryForm(enquiryForm);
    if (Object.keys(errors).length > 0) {
      setEnquiryErrors(errors);
      return;
    }

    setIsSubmittingEnquiry(true);
    try {
      const response = await submitBlogEnquiry(
        buildBlogEnquiryPayload(enquiryForm, {
          interestedIn: postData?.title || "",
        })
      );

      if (!isBlogApiSuccess(response)) {
        throw new Error(response?.message || "Unable to submit enquiry.");
      }

      showAlert({
        title: "Enquiry submitted",
        message:
          response?.message ||
          "Thank you! Our travel experts will contact you shortly.",
      });
      setEnquiryForm(createBlogEnquiryFormState());
      setEnquiryErrors({});
    } catch (error) {
      showAlert({
        title: "Submission failed",
        message: getBlogApiErrorMessage(error, "Unable to submit enquiry."),
      });
    } finally {
      setIsSubmittingEnquiry(false);
    }
  };

  return (
    <aside className={styles.blogSecRight}>
      <div className={styles.sidebarBox2}>
        <h3 className={styles.sidebarTitle}>
          <span className={styles.desktopShareText}>Share on</span>
          <span className={styles.mobileShareText}>Share</span>
        </h3>
        <div className={styles.socialRow}>
          <button
            type="button"
            className={styles.socialBtn}
            aria-label="Share on Instagram"
            onClick={onShareClick}
          >
            <RiInstagramFill />
          </button>
          <button
            type="button"
            className={styles.socialBtn}
            aria-label="Share on Facebook"
            onClick={onShareClick}
          >
            <FaFacebookF />
          </button>
          <button
            type="button"
            className={styles.socialBtn}
            aria-label="Share on LinkedIn"
            onClick={onShareClick}
          >
            <FaLinkedinIn />
          </button>
          <button
            type="button"
            className={styles.socialBtn}
            aria-label="Share on WhatsApp"
            onClick={onShareClick}
          >
            <RiWhatsappFill />
          </button>
          <button
            type="button"
            className={styles.socialBtn}
            aria-label="Share via Email"
            onClick={onShareClick}
          >
            <FaEnvelope />
          </button>
        </div>
      </div>

      <h3 className={styles.sidebarTitle}>Recent posts</h3>
      <div className={styles.sidebarBox}>
        {recentPosts.length > 0 ? (
          <div className={styles.recentList}>
            {recentPosts.map((post) => (
              <a
                key={post.id || post.slug}
                href={withRegionPath(`/blog/${post.slug}`)}
                className={styles.recentCard}
              >
                <img
                  src={post.image || "/images/blog-inner-2.jpg"}
                  alt={post.title}
                  className={styles.recentImg}
                />
                <div className={styles.recentContent}>
                  <h4 className={styles.recentPostTitle}>{post.title}</h4>
                  <div className={styles.recentMeta}>
                    {post.date ? (
                      <span>
                        <FaCalendar size={12} /> {post.date}
                      </span>
                    ) : null}
                    {post.views ? (
                      <span>
                        <FaEye size={14} /> {post.views}
                      </span>
                    ) : null}
                  </div>
                </div>
              </a>
            ))}
          </div>
        ) : (
          <p className="nodata">No recent posts found</p>
        )}
      </div>

      <div className={styles.sidebarBox2}>
        <h3 className={styles.sidebarTitle}>
          Get the Latest Travel Updates!
        </h3>
        <form onSubmit={handleSubscribeSubmit} className={styles.newsletterForm}>
          <div className={styles.newsletterField}>
            <input
              type="email"
              placeholder="Enter your email address..."
              value={subscribeForm.emailId}
              onChange={(event) =>
                updateSubscribeField("emailId", event.target.value)
              }
              className={`nh-input-radius-pill ${styles.newsletterInput} ${
                subscribeErrors.emailId ? styles.newsletterInputError : ""
              }`.trim()}
            />
            {subscribeErrors.emailId ? (
              <span className={styles.fieldError}>{subscribeErrors.emailId}</span>
            ) : null}
          </div>
          <button
            type="submit"
            className={styles.subscribeBtn}
            disabled={isSubmittingSubscribe}
          >
            {isSubmittingSubscribe ? "Please wait..." : "Subscribe Us"}
          </button>
        </form>
      </div>

      <div className={styles.tagsContainer}>
        {tags.map((tag, idx) => (
          <span key={idx} className={styles.tagPill}>
            {tag}
          </span>
        ))}
      </div>

      <div className={styles.sidebarBox2}>
        <h3 className={styles.formMainHeading}>
          Fill form for best travel deals!
        </h3>
        <form onSubmit={handleEnquirySubmit} className={styles.enquiryForm} noValidate>
          <div className={styles.formField}>
            <fieldset
              className={`${styles.inputGroup} ${
                enquiryErrors.fullName ? styles.inputGroupError : ""
              }`.trim()}
            >
              <legend className={styles.inputLegend}>Full name</legend>
              <input
                type="text"
                placeholder="Your Full Name"
                value={enquiryForm.fullName}
                onChange={(event) =>
                  updateEnquiryField("fullName", event.target.value)
                }
                className={styles.formControl}
                aria-invalid={Boolean(enquiryErrors.fullName)}
              />
            </fieldset>
            {enquiryErrors.fullName ? (
              <span className={styles.fieldError} role="alert">
                {enquiryErrors.fullName}
              </span>
            ) : null}
          </div>

          <div className={styles.formField}>
            <div className={styles.phoneFieldWrap}>
              <span className={styles.phoneFieldLabel}>Phone number</span>
              <PhoneInputField
                value={enquiryForm.phone}
                onChange={(value) => updateEnquiryField("phone", value || "")}
                error={Boolean(enquiryErrors.phone)}
                placeholder="Phone Number"
                className={styles.phoneField}
              />
            </div>
            {enquiryErrors.phone ? (
              <span className={styles.fieldError} role="alert">
                {enquiryErrors.phone}
              </span>
            ) : null}
          </div>

          <div className={styles.formField}>
            <fieldset
              className={`${styles.inputGroup} ${
                enquiryErrors.email ? styles.inputGroupError : ""
              }`.trim()}
            >
              <legend className={styles.inputLegend}>Email</legend>
              <input
                type="email"
                placeholder="Your Email Address"
                value={enquiryForm.email}
                onChange={(event) =>
                  updateEnquiryField("email", event.target.value)
                }
                className={styles.formControl}
                aria-invalid={Boolean(enquiryErrors.email)}
              />
            </fieldset>
            {enquiryErrors.email ? (
              <span className={styles.fieldError} role="alert">
                {enquiryErrors.email}
              </span>
            ) : null}
          </div>

          <div className={styles.formField}>
            <div
              className={`${styles.dateFieldWrap} ${
                enquiryErrors.travelDate ? styles.dateFieldError : ""
              }`.trim()}
            >
              <DatePickerField
                label="Travel date *"
                value={enquiryForm.travelDate}
                onChange={(value) => updateEnquiryField("travelDate", value)}
                error={Boolean(enquiryErrors.travelDate)}
              />
            </div>
            {enquiryErrors.travelDate ? (
              <span className={styles.fieldError} role="alert">
                {enquiryErrors.travelDate}
              </span>
            ) : null}
          </div>

          <div className={styles.formField}>
            <fieldset
              className={`${styles.inputGroup} ${
                enquiryErrors.noOfTravellers ? styles.inputGroupError : ""
              }`.trim()}
            >
              <legend className={styles.inputLegend}>No. of travelers</legend>
              <select
                value={enquiryForm.noOfTravellers}
                onChange={(event) =>
                  updateEnquiryField("noOfTravellers", event.target.value)
                }
                className={`${styles.formControl} ${styles.formControlSelect}`}
                aria-invalid={Boolean(enquiryErrors.noOfTravellers)}
              >
                <option value="" disabled>
                  Select
                </option>
                {BLOG_TRAVELLER_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </fieldset>
            {enquiryErrors.noOfTravellers ? (
              <span className={styles.fieldError} role="alert">
                {enquiryErrors.noOfTravellers}
              </span>
            ) : null}
          </div>

          <div className={styles.formField}>
            <fieldset className={styles.inputGroup}>
              <legend className={styles.inputLegend}>What is on your mind?</legend>
              <textarea
                placeholder="Type here..."
                rows={3}
                value={enquiryForm.description}
                onChange={(event) =>
                  updateEnquiryField("description", event.target.value)
                }
                className={styles.formControlTextarea}
              />
            </fieldset>
          </div>

          <button
            type="submit"
            className={styles.submitBtn}
            disabled={isSubmittingEnquiry}
          >
            {isSubmittingEnquiry ? "Submitting..." : "Submit"}
          </button>
        </form>
      </div>
    </aside>
  );
}
